
import { Component, Vue } from 'vue-property-decorator';
import DateRangeFilter from '@/components/automation-dashboard/DateRangeFilter.vue';
import SummaryStatistics from '@/components/automation-dashboard/SummaryStatistics.vue';
import PerformanceChart, { PerformanceChartData } from '@/components/automation-dashboard/PerformanceChart.vue';
import GenerationHistoryTable, { GenerationHistoryData, HistoryItem } from '@/components/automation-dashboard/GenerationHistoryTable.vue';
import ExecutionDetailModal from '@/components/automation-dashboard/ExecutionDetailModal.vue';
import { getFeatureVersion, setFeatureVersion } from '@/config/atlasFeatures';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import CopyLinkButton from '@/components/CopyLinkButton.vue';


interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

// Placeholder type for summary data, will be refined with actual data structure
interface SummaryData {
  totalPOsCreated?: number;
  totalValue?: number;
  totalItems?: number;
  approvalRate?: number;
  totalApprovedPOs?: number;
  currencySymbol?: string;
}

@Component({
  name: 'AutomationDashboard',

  components: {
    CopyLinkButton,
    DateRangeFilter,
    SummaryStatistics,
    PerformanceChart,
    GenerationHistoryTable,
    ExecutionDetailModal,
    TryNewAtlasBanner,
    // TODO: Import and register other components:
    // DashboardHeader,
  }
})
export default class AutomationDashboard extends Vue {
  
  private isReady = false;
  private selectedDateRange: DateRange = { startDate: null, endDate: null };
  
  // Store real API data
  private apiSummaryData: SummaryData = {
    totalPOsCreated: 0,
    totalValue: 0,
    totalItems: 0,
    approvalRate: 0,
    totalApprovedPOs: 0,
    currencySymbol: '$',
  };

  private apiChartData: PerformanceChartData = {
    labels: [],
    datasets: [],
  };

  private apiHistoryData: GenerationHistoryData = {
    rows: [],
    count: 0,
  };

  private historyTableOptions = {
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  };

  private historyLoading = false;
  private summaryLoading = false;
  private chartLoading = false;
  private error: string | null = null;

  // Execution detail modal properties
  private showExecutionDetailModal = false;
  private selectedExecutionId: number | null = null;

  get showTryNewBanner(): boolean {
    return getFeatureVersion('automatedPO') === 'classic';
  }

  switchToAtlas(): void {
    setFeatureVersion('automatedPO', 'atlas');
    this.$router.push('/assistant/automated-po');
  }

  // Computed properties
  get summaryData(): SummaryData {
    return this.apiSummaryData;
  }

  get chartData(): PerformanceChartData {
    return this.apiChartData;
  }

  get historyData(): GenerationHistoryData {
    return this.apiHistoryData;
  }

  // Methods
  private async onDateRangeChange(newRange: DateRange): Promise<void> {
    this.selectedDateRange = newRange;
    
    // Fetch new data based on the selected date range
    await this.fetchDashboardData();
  }

  private handleHistoryRowClick(item: HistoryItem): void {
    // Open execution detail modal
    this.selectedExecutionId = item.executionId;
    this.showExecutionDetailModal = true;
  }

  private async handleHistoryOptionsUpdate(options: any): Promise<void> {
    // Create a new object to avoid reference issues
    this.historyTableOptions = { ...options };
    
    // Fetch new history data based on options (pagination, sorting)
    await this.fetchDashboardData();
  }

  private handleConfigClick(item: HistoryItem): void {
    // Navigate to the PO automation config detail page
    this.$router.push({
      name: 'po-automation-config-detail',
      params: { id: String(item.configId) }
    });
  }

  private handleViewDetails(item: HistoryItem): void {
    // Open execution detail modal
    this.selectedExecutionId = item.executionId;
    this.showExecutionDetailModal = true;
  }

  private transformTimeseriesForChart(timeseries: any[]): PerformanceChartData {
    if (!timeseries || timeseries.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = timeseries.map(d => new Date(d.period).toLocaleDateString());
    const successfulData = timeseries.map(d => d.successful || 0);
    const failedData = timeseries.map(d => d.failed || 0);

    const result = {
      labels,
      datasets: [
        {
          label: 'Successful Runs',
          data: successfulData,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
        },
        {
          label: 'Failed Runs',
          data: failedData,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1,
        },
      ],
    };
    
    return result;
  }

  private async fetchDashboardData(): Promise<void> {
    try {
      // For now, we'll use the service directly since Vuex integration needs more setup
      const { poAutomationAnalyticsService } = await import('@/services/po-automation-analytics.service');
      
      // Set loading states
      this.summaryLoading = true;
      this.chartLoading = true;
      this.historyLoading = true;
      this.error = null;

      // Fetch data from API
      const [analyticsData, historyData] = await Promise.all([
        poAutomationAnalyticsService.getAnalytics(
          this.selectedDateRange.startDate || '',
          this.selectedDateRange.endDate || ''
        ),
        poAutomationAnalyticsService.getGenerationHistory({
          page: this.historyTableOptions.page,
          limit: this.historyTableOptions.itemsPerPage,
          sortBy: this.historyTableOptions.sortBy[0] || 'runDate',
          sortOrder: this.historyTableOptions.sortDesc[0] ? 'desc' : 'asc',
        })
      ]);

      // Update the data with real API responses
      if (analyticsData && analyticsData.summary) {
        // Map summary data from the API's 'summary' object
        this.apiSummaryData = {
          totalPOsCreated: analyticsData.summary.totalPurchaseOrders || 0,
          totalValue: analyticsData.summary.totalValue || 0,
          totalItems: analyticsData.summary.totalItems || 0,
          approvalRate: analyticsData.summary.approvalRate || 0,
          totalApprovedPOs: analyticsData.summary.totalApprovedPOs || 0,
          currencySymbol: '$', // Assuming a default currency
        };

        // Transform and update chart data from the API's 'timeSeries' array (note the capital S)
        this.apiChartData = this.transformTimeseriesForChart(analyticsData.timeSeries);
      }

      if (historyData) {
        // Update history data
        this.apiHistoryData = {
          rows: historyData.rows || [],
          count: historyData.count || 0,
        };
      }

    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      this.error = err.message || 'Failed to fetch dashboard data';
      
      // Set fallback data when API fails
      this.apiSummaryData = {
        totalPOsCreated: 0,
        totalValue: 0,
        totalItems: 0,
        approvalRate: 0,
        totalApprovedPOs: 0,
        currencySymbol: '$',
      };

      this.apiChartData = {
        labels: ['No data available'],
        datasets: [],
      };

      this.apiHistoryData = {
        rows: [],
        count: 0,
      };
    } finally {
      this.summaryLoading = false;
      this.chartLoading = false;
      this.historyLoading = false;
    }
  }

  // Execution detail modal handlers
  private handleExecutionDetailClose(): void {
    this.showExecutionDetailModal = false;
    this.selectedExecutionId = null;
  }

  private handleExportComplete(): void {
    // Show success notification
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('showSnackbar', {
        message: 'Purchase orders exported successfully',
        color: 'success'
      });
    }
  }

  private handleExportError(error: any): void {
    console.error('Export failed:', error);
    // Show error notification
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('showSnackbar', {
        message: 'Failed to export purchase orders',
        color: 'error'
      });
    }
  }

  // Lifecycle hooks
  public async mounted(): Promise<void> {
    
    // Set initial date range to last 7 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    this.selectedDateRange = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
    
    // Fetch initial data
    await this.fetchDashboardData();
    
    // Mark component as ready after initialization
    setTimeout(() => {
      this.isReady = true;
    }, 100);

    // Watch for errors and display them
    if (this.error) {
      console.error('Dashboard error:', this.error);
      // TODO: Show error notification to user
    }
  }
}
